import React from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { ProgressSpinner } from 'primereact/progressspinner';
import LmsNewsCard from './components/LmsNewsCard';
import LmsCodeDropdown from '../components/LmsCodeDropdown';
import useLmsNewsData from './hooks/useLmsNewsData';
import { useLanguage } from '../../../contexts/LanguageContext';
import { PRIMARY } from '../components/LmsSectionCard';

const ROWS_PER_PAGE_OPTIONS = [12, 24, 48];

const LmsNewsPage = () => {
    const { th } = useLanguage();
    const { items, totalCount, isLoading, isFetching, lazyParams, onPage, onRowsChange, filters, searchInput, setSearchInput, handleFilterChange, clearFilters, hasActiveFilters } = useLmsNewsData();

    const sortOptions = [
        { label: th('lmsNews.sortNewest'), value: 'desc' },
        { label: th('lmsNews.sortOldest'), value: 'asc' }
    ];

    const rangeStart = totalCount === 0 ? 0 : lazyParams.first + 1;
    const rangeEnd = Math.min(lazyParams.first + lazyParams.rows, totalCount);
    const busy = isLoading || isFetching;

    return (
        <div>
            <div className="mb-4">
                <h1 style={{ color: PRIMARY, margin: 0, fontSize: '1.75rem' }}>{th('lmsNews.title')}</h1>
                <span className="text-color-secondary block mt-1">{th('lmsNews.subtitle')}</span>
            </div>

            <div
                className="flex align-items-center justify-content-between mb-4 flex-wrap gap-3"
                style={{ backgroundColor: '#F9FAFB', border: '1px solid #EAECF0', borderRadius: '1.25rem', padding: '1rem 1.25rem' }}
            >
                <div className="flex align-items-center gap-3 flex-wrap">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={th('lmsNews.searchPlaceholder')} className="border-round-3xl" style={{ width: '16rem' }} />
                    </span>

                    <LmsCodeDropdown
                        groupCode="TRAINING_LOCATION"
                        value={filters.planningLocation}
                        onChange={(value) => handleFilterChange('planningLocation', value)}
                        placeholder={th('lmsNews.allLocations')}
                        className="border-round-3xl w-12rem"
                    />

                    <LmsCodeDropdown
                        groupCode="TRAINING_TYPES"
                        value={filters.trainingType}
                        onChange={(value) => handleFilterChange('trainingType', value)}
                        placeholder={th('lmsNews.allTrainingTypes')}
                        className="border-round-3xl w-12rem"
                    />

                    <Dropdown value={filters.sortOrder} options={sortOptions} onChange={(e) => handleFilterChange('sortOrder', e.value)} className="border-round-3xl w-11rem" />
                </div>

                {hasActiveFilters && <Button label={th('lmsNews.clearFilters')} text onClick={clearFilters} />}
            </div>

            {busy && (
                <div className="flex align-items-center justify-content-center" style={{ minHeight: '12rem' }}>
                    <ProgressSpinner style={{ width: '3rem', height: '3rem' }} strokeWidth="4" />
                </div>
            )}

            {!busy && items.length === 0 && (
                <div className="flex flex-column align-items-center gap-2 p-5 text-color-secondary">
                    <i className="pi pi-inbox" style={{ fontSize: '2rem' }} />
                    <span>{th('lmsNews.emptyMessage')}</span>
                </div>
            )}

            {!busy && items.length > 0 && (
                <>
                    <div className="grid">
                        {items.map((news) => (
                            <LmsNewsCard key={news.id} news={news} />
                        ))}
                    </div>

                    <div className="flex align-items-center justify-content-between mt-4 flex-wrap gap-3">
                        <span className="text-color-secondary text-sm">{th('lmsNews.paginationReport', { first: rangeStart, last: rangeEnd, totalRecords: totalCount })}</span>

                        <Paginator first={lazyParams.first} rows={lazyParams.rows} totalRecords={totalCount} template="PrevPageLink PageLinks NextPageLink" onPageChange={onPage} className="border-none" style={{ background: 'transparent' }} />

                        <div className="flex align-items-center gap-2">
                            <span className="text-color-secondary text-sm">{th('lmsNews.rowsPerPageLabel')}</span>
                            <Dropdown value={lazyParams.rows} options={ROWS_PER_PAGE_OPTIONS} onChange={(e) => onRowsChange(e.value)} className="w-6rem" />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default LmsNewsPage;
